// FILE: app/(main)/exams/[slug]/ExamDetailClient.tsx
"use client";

import { createPortal } from "react-dom";
import ExamLeaderboard from "@/components/exams/ExamLeaderboard";
import DetailBreadcrumb, { type DetailBreadcrumbItem } from "@/components/seo/DetailBreadcrumb";
import { buildLandingUrl } from "@/lib/seo/slug";
import { useState, useEffect } from "react";
import { consumePostLoginIntent } from "@/lib/postLoginAction";
import { useTopupReturn } from "@/hooks/useTopupReturn";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useAuth } from "@/lib/AuthContext";
import { useDiamonds } from "@/lib/DiamondsContext";
import PurchaseConfirmModal from "@/components/shared/PurchaseConfirmModal";
import {
  Loader2, Clock, BookOpen, Star, Gem, Users, CheckCircle2,
  Play, Share2, ChevronRight, Unlock, Target, Eye,
  Lightbulb, BarChart2,
} from "lucide-react";
import ExamFullscreen from "@/components/study/ExamFullscreen";

type TabId = "overview" | "demo" | "stats";

interface CategoryContext {
  domain:   { name: string; slug: string } | null;
  org:      { name: string; slug: string } | null;
  position: { name: string; slug: string } | null;
}

interface Props {
  exam: any;
  sections: { name: string; count: number }[];
  creator: any;
  sameAuthorExams: any[];
  user: any;
  isPurchased: boolean;
  categoryContext?: CategoryContext;
  isOwner?: boolean;
  draft?: { current_index: number; answers: any[]; time_spent: number } | null;
  authReady?: boolean;
}

export default function ExamDetailClient({
  exam,
  sections,
  creator,
  sameAuthorExams,
  user,
  isPurchased: initialPurchased,
  categoryContext,
  isOwner = false,
  draft = null,
  authReady = true,
}: Props) {
  const router = useRouter();
  const [isPurchased, setIsPurchased] = useState(initialPurchased);
  const [purchasing, setPurchasing] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [studyOpen, setStudyOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { diamonds, addDiamonds } = useDiamonds();
  const { requireAuth, user: authCtxUser } = useAuth() as any;
  // Login bằng modal (không reload) → prop `user` từ server chưa đổi; dùng context để bắt kịp
  const currentUser = authCtxUser ?? user;
  const [reviewSubmission, setReviewSubmission] = useState<any>(null);

  // Login xong (OAuth reload) → tiếp ý định mua
  useEffect(() => {
    if (!currentUser) return;
    const intent = consumePostLoginIntent();
    if (intent?.action === "enroll-exam") setShowConfirm(true);
  }, [currentUser]);

  // Quay về từ app ngân hàng → mở confirm mua
  useTopupReturn(!!currentUser, () => setShowConfirm(true));

  // Link có ?coupon=CODE (mail upsell, chiến dịch) → đã login + chưa mua thì mở modal, mã tự áp dụng
  const [urlCoupon, setUrlCoupon] = useState<string | undefined>(undefined);
  useEffect(() => {
    const c = new URLSearchParams(window.location.search).get("coupon");
    if (!c) return;
    setUrlCoupon(c);
    if (currentUser && !initialPurchased && !isOwner && !(exam.price === 0 || exam.is_free)) setShowConfirm(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const handlePurchase = async (couponCode?: string) => {
    if (!exam || !currentUser) return;
    setPurchasing(true);
    try {
      const { purchaseExam } = await import("@/actions/study");
      const result = await purchaseExam(exam.id, couponCode);
      if (!result.success) {
        alert(result.error ?? "Có lỗi xảy ra");
        setPurchasing(false);
        return;
      }
      // Trừ topbar (countdown animation tự chạy nếu topbar dùng useCountUp)
      const paid = result.paid ?? exam.price ?? 0;
      if (paid > 0 && !result.already) {
        addDiamonds(-paid, "buy_exam");
      }
      setIsPurchased(true);
      // Vào thẳng giao diện thi
      setStudyOpen(true);
    } catch {
      alert("Lỗi kết nối. Vui lòng thử lại.");
    }
    setPurchasing(false);
  };

  const isFree = exam.price === 0 || exam.is_free;
  const completionRate = exam.attempt_count > 0 ? 78 : 0; // TODO: tính từ data thật
  const passScore = 50; // TODO: từ exam_structures
  const totalSections = sections.reduce((s, sec) => s + sec.count, 0);

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {/* Study Fullscreen */}
      {studyOpen && typeof document !== "undefined" && createPortal(
        <ExamFullscreen
          examId={exam.id}
          resumeFromDraft={draft}
          reviewData={reviewSubmission ? {
            questions: (reviewSubmission.answers ?? []).map((a: any) => ({
              id: a.question_id,
              selected: a.selected,
              is_correct: a.is_correct,
            })),
            userName: reviewSubmission.user?.full_name ?? "Ẩn danh",
          } : null}
          onExit={() => { setStudyOpen(false); setReviewSubmission(null); router.refresh(); }}
        />,
        document.body
      )}

      {!studyOpen && (
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Breadcrumb */}
        <DetailBreadcrumb items={buildExamBreadcrumbs(exam, categoryContext)} />
        
        {/* Resume banner */}
        {draft && !isOwner && isPurchased && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-2xl shrink-0">⏸️</div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-bold text-amber-900">Bạn có bài làm dở</p>
              <p className="text-[11px] text-amber-700">Đang ở câu {(draft.current_index ?? 0) + 1} · Đã làm {draft.answers?.length ?? 0} câu</p>
            </div>
            <button onClick={() => { if (requireAuth()) setStudyOpen(true); }}
              className="px-4 py-2 rounded-full bg-amber-500 text-white text-[12px] font-bold hover:bg-amber-600 transition-colors shrink-0">
              Tiếp tục
            </button>
          </div>
        )}

        {/* ══ HERO BANNER ══ */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0f2a3a] via-[#1a4a60] to-[#1a5570] p-6 sm:p-8">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_40%,rgba(249,115,22,.12),transparent_55%)] pointer-events-none" />
          <div className="relative">
            {/* Tags */}
            <div className="flex items-center gap-2 flex-wrap mb-4">
              {isOwner && (
                <span className="px-3 py-1 rounded-full text-[10px] font-bold bg-violet-500/20 text-violet-200 border border-violet-400/40">
                  👑 Tác giả
                </span>
              )}
              {exam.org?.name && (
                <span className="px-3 py-1 rounded-full text-[10px] font-bold bg-white/10 text-white/80 border border-white/10">{exam.org.name}</span>
              )}
              {exam.position?.name && (
                <span className="px-3 py-1 rounded-full text-[10px] font-bold bg-white/10 text-white/80 border border-white/10">{exam.position.name}</span>
              )}
              {(exam.tags ?? []).slice(0, 3).map((t: string) => (
                <span key={t} className="px-3 py-1 rounded-full text-[10px] font-bold bg-white/10 text-white/80 border border-white/10">{t}</span>
              ))}
            </div>

            {/* Title row */}
            <div className="flex items-start gap-4 mb-3">
              <span className="text-4xl sm:text-5xl shrink-0">{exam.emoji ?? "📝"}</span>
              <div className="flex-1 min-w-0">
                <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-white leading-tight">{exam.title}</h1>
                <p className="text-[13px] text-white/50 mt-1.5">
                  by <span className="text-white/70 font-semibold">{creator?.full_name ?? creator?.username ?? "Tác giả"}</span>
                </p>
              </div>
            </div>

            {/* Stats row */}
            <div className="flex items-center gap-4 flex-wrap text-[11px] text-white/50">
              {Number(exam.rating) > 0 && (
                <span className="flex items-center gap-1 text-amber-300 font-bold">
                  <Star className="w-3.5 h-3.5 fill-current" />{Number(exam.rating).toFixed(1)}
                  <span className="text-white/30 font-normal">({exam.rating_count ?? 0})</span>
                </span>
              )}
              <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{(exam.attempt_count ?? 0).toLocaleString()} lượt thi</span>
              <span className="flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" />{exam.total_questions} câu</span>
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{exam.time_limit} phút</span>
              {completionRate > 0 && (
                <span className="flex items-center gap-1"><Target className="w-3.5 h-3.5" />{completionRate}% hoàn thành</span>
              )}
            </div>
          </div>
        </div>

        {/* ══ TABS ══ */}
        <div className="flex gap-2">
          <button onClick={() => setActiveTab("overview")}
            className={`px-5 py-2.5 rounded-xl text-[12px] font-bold transition-all flex items-center gap-1.5 ${
              activeTab === "overview" ? "bg-teal-800 text-white shadow-sm" : "bg-white border border-slate-200 text-muted-foreground hover:bg-muted"
            }`}>
            <BookOpen className="w-3.5 h-3.5" />Tổng quan
          </button>
          <button onClick={() => setActiveTab("demo")}
            className={`px-5 py-2.5 rounded-xl text-[12px] font-bold transition-all flex items-center gap-1.5 ${
              activeTab === "demo" ? "bg-teal-800 text-white shadow-sm" : "bg-white border border-slate-200 text-muted-foreground hover:bg-muted"
            }`}>
            <Eye className="w-3.5 h-3.5" />Câu hỏi Demo
          </button>
          <button onClick={() => setActiveTab("stats")}
            className={`px-4 py-2 rounded-xl text-[12px] font-bold transition-all ${
              activeTab === "stats" ? "bg-teal-800 text-white shadow-sm" : "bg-white border border-slate-200 text-muted-foreground hover:bg-muted"
            }`}>
            🏆 Thống kê
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ── LEFT COLUMN ── */}
          <div className="lg:col-span-2 space-y-4">
            {activeTab === "overview" ? (
              <>
                {/* Giới thiệu */}
                {exam.description && (
                  <div className="bg-white rounded-2xl border border-slate-200 p-5">
                    <h3 className="text-[14px] font-extrabold text-teal-800 flex items-center gap-2 mb-3">
                      <Lightbulb className="w-4 h-4 text-amber-500" />Giới thiệu đề thi
                    </h3>
                    <p className="text-[13px] text-[#475569] leading-relaxed">{exam.description}</p>
                  </div>
                )}

                {/* Cấu trúc đề thi */}
                {sections.length > 0 && (
                  <div className="bg-white rounded-2xl border border-slate-200 p-5">
                    <h3 className="text-[14px] font-extrabold text-teal-800 flex items-center gap-2 mb-4">
                      <BarChart2 className="w-4 h-4 text-teal-500" />Cấu trúc đề thi
                    </h3>
                    <div className="space-y-2">
                      {sections.map((sec, i) => (
                        <div key={sec.name} className="flex items-center gap-3 py-2.5 px-3 rounded-xl bg-[#f8fafc] border border-[#f1f5f9]">
                          <span className="text-lg shrink-0">{["📚", "🏦", "🔍", "📊", "⚖️", "💼"][i % 6]}</span>
                          <span className="flex-1 text-[12px] font-semibold text-[#334155]">{sec.name}</span>
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-teal-50 text-teal-700 border border-teal-200">{sec.count} câu</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                      <span className="text-[12px] font-semibold text-muted-foreground">Tổng cộng</span>
                      <span className="text-[13px] font-extrabold text-teal-800">{totalSections} câu — {exam.time_limit} phút</span>
                    </div>
                  </div>
                )}

                {/* Điều kiện đạt */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                  <h3 className="text-[14px] font-extrabold text-teal-800 flex items-center gap-2 mb-4">
                    <Target className="w-4 h-4 text-orange-500" />Điều kiện đạt
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                      <p className="text-[28px] font-extrabold text-emerald-700 leading-none mb-1">{passScore}%</p>
                      <p className="text-[11px] text-emerald-600 font-medium">Điểm đạt tối thiểu</p>
                    </div>
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center">
                      <p className="text-[28px] font-extrabold text-orange-600 leading-none mb-1">{exam.time_limit} phút</p>
                      <p className="text-[11px] text-orange-500 font-medium">Thời gian làm bài</p>
                    </div>
                  </div>
                </div>
              </>
            ) : activeTab === "demo" ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <h3 className="text-[14px] font-extrabold text-teal-800 flex items-center gap-2 mb-3">
                  <Unlock className="w-4 h-4 text-emerald-500" />Câu hỏi Demo
                </h3>
                <p className="text-[13px] text-muted-foreground mb-4">
                  {exam.demo_limit > 0
                    ? `Có ${exam.demo_limit} câu hỏi demo miễn phí để bạn trải nghiệm trước.`
                    : "Đề thi này chưa có câu hỏi demo."}
                </p>
                {exam.demo_limit > 0 && (
                  <button onClick={() => setStudyOpen(true)}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-emerald-600 text-white text-[13px] font-bold hover:bg-emerald-500 transition-colors">
                    <Play className="w-4 h-4" />Làm thử {exam.demo_limit} câu Demo
                  </button>
                )}
              </div>
            ) : activeTab === "stats" ? (
              <ExamLeaderboard examId={exam.id} isCreator={currentUser?.id === exam.user_id} onViewSubmission={(sub) => { setReviewSubmission(sub); setStudyOpen(true); }} />
            ) : null}
          </div>
          {/* ── RIGHT SIDEBAR ── */}
          <div className="space-y-4 lg:sticky lg:top-6 lg:self-start">
            {/* Action Card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              {/* Price */}
              <div className="text-center mb-4">
                {isFree ? (
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-200">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    <span className="text-[16px] font-extrabold text-emerald-700">Miễn phí</span>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-center gap-2">
                      <Gem className="w-6 h-6 text-amber-500" />
                      <span className="text-[32px] font-extrabold text-[#0f172a]">{exam.price}</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">kim cương</p>
                  </div>
                )}
              </div>

              {/* Quick stats */}
              <div className="space-y-2.5 mb-5">
                {[
                  { icon: BookOpen, label: "Tổng số câu hỏi", value: `${exam.total_questions} câu` },
                  { icon: Clock, label: "Thời gian làm bài", value: `${exam.time_limit} phút` },
                  { icon: Target, label: "Điểm đạt tối thiểu", value: `${passScore}%` },
                  { icon: Users, label: "Lượt thi", value: (exam.attempt_count ?? 0).toLocaleString() },
                  { icon: Star, label: "Đánh giá", value: Number(exam.rating) > 0 ? `${Number(exam.rating).toFixed(1)} ★ (${exam.rating_count ?? 0})` : "Chưa có" },
                ].map(({ icon: Icon, label, value }) => (
                  <div key={label} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                    <span className="flex items-center gap-2 text-[11px] text-muted-foreground"><Icon className="w-3.5 h-3.5" />{label}</span>
                    <span className="text-[12px] font-bold text-[#0f172a]">{value}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              {isOwner ? (
                <>
                  <button onClick={() => setStudyOpen(true)}
                    className="w-full py-3 rounded-full bg-teal-700 text-white text-[14px] font-bold hover:bg-teal-600 transition-all flex items-center justify-center gap-2 shadow-[0_3px_12px_rgba(15,118,110,.35)]">
                    <Play className="w-4 h-4" />Vào làm thử (Chủ)
                  </button>
                  <a href={`/studio/exams/${exam.id}`}
                    className="w-full mt-2.5 py-2.5 rounded-full border border-teal-300 text-[12px] font-bold text-teal-700 hover:bg-teal-50 flex items-center justify-center gap-2 transition-colors no-underline">
                    ✏️ Chỉnh sửa đề
                  </a>
                </>
              ) : isPurchased ? (
                <button onClick={() => { if (requireAuth()) setStudyOpen(true); }}
                  className="w-full py-3 rounded-full bg-orange-500 text-white text-[14px] font-bold hover:bg-orange-600 transition-all flex items-center justify-center gap-2 shadow-[0_3px_12px_rgba(249,115,22,.35)]">
                  <Play className="w-4 h-4" />
                  {draft ? `Tiếp tục (câu ${(draft.current_index ?? 0) + 1})` : "Bắt đầu thi"}
                </button>
              ) : (
                <button
                  onClick={() => {
  if (!requireAuth()) return;
  if (isFree) { setStudyOpen(true); return; }
  setShowConfirm(true);
}}
                  disabled={purchasing}
                  className="w-full py-3 rounded-full bg-orange-500 text-white text-[14px] font-bold hover:bg-orange-600 transition-all flex items-center justify-center gap-2 shadow-[0_3px_12px_rgba(249,115,22,.35)]"
                >
                  {purchasing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gem className="w-4 h-4" />}
                  {isFree ? "Bắt đầu thi miễn phí" : "Mở khóa & Bắt đầu thi"}
                </button>
              )}

              {/* Share */}
              <button className="w-full mt-2.5 py-2.5 rounded-full border border-slate-200 text-[12px] font-semibold text-muted-foreground hover:bg-muted flex items-center justify-center gap-2 transition-colors">
                <Share2 className="w-3.5 h-3.5" />Chia sẻ đề thi
              </button>
            </div>

            {/* Creator Card */}
            {creator && (
              <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <h3 className="text-[11px] font-bold text-muted-foreground uppercase tracking-wide mb-3">Tác giả</h3>
                <div className="flex items-center gap-3 mb-3">
                  {creator.avatar_url ? (
                    <img src={creator.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-slate-200" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center text-[13px] font-bold text-teal-700">
                      {(creator.full_name ?? "T")[0]}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-bold text-[#0f172a] truncate">{creator.full_name ?? creator.username}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">✓ Verified</span>
                      <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200">★ Top Creator</span>
                    </div>
                  </div>
                </div>
                <Link href={`/${creator.username ?? creator.id}`}
                  className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl border border-slate-200 text-[11px] font-semibold text-muted-foreground hover:bg-muted transition-colors">
                  Xem trang cá nhân <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
            )}

            {/* Same Author Exams */}
            {sameAuthorExams.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <h3 className="text-[11px] font-bold text-muted-foreground uppercase tracking-wide mb-3">Đề thi cùng tác giả</h3>
                <div className="space-y-2">
                  {sameAuthorExams.map((e: any) => (
                    <Link key={e.id} href={`/exams/${e.slug || e.id}`}
                      className="flex items-center gap-2.5 p-2.5 rounded-xl border border-slate-100 hover:border-teal-300 hover:bg-teal-50/30 transition-all">
                      <span className="text-lg shrink-0">{e.emoji ?? "📝"}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-bold text-teal-800 truncate">{e.title}</p>
                        <span className="text-[10px] text-muted-foreground">{e.total_questions} câu</span>
                      </div>
                      {e.price === 0 ? (
                        <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full shrink-0">Free</span>
                      ) : (
                        <span className="text-[9px] font-bold text-orange-600 shrink-0">{e.price} 💎</span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      )}

      {/* ── Purchase Confirm Modal ── */}
      <PurchaseConfirmModal
        open={showConfirm}
        type="exam"
        title={exam.title}
        price={exam.price ?? 0}
        hasDemo={(exam.demo_limit ?? 0) > 0}
        userBalance={diamonds}
        onConfirm={async (couponCode?: string) => {
          setShowConfirm(false);
          await handlePurchase(couponCode);
        }}
        onViewDemo={() => setStudyOpen(true)}
        onClose={() => setShowConfirm(false)}
        itemId={exam.id}
        initialCoupon={urlCoupon}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// BREADCRUMB BUILDER
// ═══════════════════════════════════════════════════════════════
function buildExamBreadcrumbs(
  exam: any,
  ctx?: { domain: any; org: any; position: any }
): DetailBreadcrumbItem[] {
  const items: DetailBreadcrumbItem[] = [
    { label: "Đề thi", href: "/exams" },
  ];

  if (ctx?.domain) {
    items.push({
      label: ctx.domain.name,
      href: buildLandingUrl("exams", { domain: ctx.domain.slug }),
    });
  }
  if (ctx?.org && ctx.domain) {
    items.push({
      label: ctx.org.name,
      href: buildLandingUrl("exams", {
        domain: ctx.domain.slug,
        org: ctx.org.slug,
      }),
    });
  }
  if (ctx?.position && ctx.domain && ctx.org) {
    items.push({
      label: ctx.position.name,
      href: buildLandingUrl("exams", {
        domain: ctx.domain.slug,
        org: ctx.org.slug,
        position: ctx.position.slug,
      }),
    });
  }

  items.push({ label: exam?.title ?? "Đề thi" });

  return items;
}
